import { useState } from 'react';
import { X, ArrowLeft, MapPin, Calendar, Maximize2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface CompleteDeliveryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

export default function CompleteDeliveryModal({
  isOpen,
  onClose,
  onComplete
}: CompleteDeliveryModalProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'photo' | 'map'>('photo');
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleRateOrder = () => {
    onClose();
    navigate('/order-review');
  };

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {isExpanded ? (
        <>
          {/* Header with back button and title */}
          <div className="py-2 flex items-center">
            <button onClick={() => setIsExpanded(false)} className="border-none bg-transparent">
              <ArrowLeft size={24} />
            </button>
            <h2 className="font-semibold flex-1 text-center">Your order</h2>
            <button onClick={() => setIsExpanded(false)} className="invisible">
              <ArrowLeft size={24} />
            </button>
          </div>

          <div className="flex relative border-b border-gray-200">
            <button 
              className={`flex-1 py-3 text-center font-medium border-none bg-white`}
              onClick={() => setActiveTab('photo')}
            >
              Photo
            </button>
            <button 
              className={`flex-1 py-3 text-center font-medium border-none bg-white`}
              onClick={() => setActiveTab('map')}
            >
              Map
            </button>
            {/* Active tab indicator */}
            <div 
              className="absolute bottom-0 h-[3px] bg-black transition-all duration-300 ease-in-out"
              style={{ 
                left: activeTab === 'photo' ? '0%' : '50%',
                width: '50%'
              }}
            />
          </div>
          
          {/* Content based on active tab */}
          {activeTab === 'photo' ? (
            <div className="flex-1 bg-gray-100 flex items-center justify-center">
              <div className="text-center text-gray-400">
                <p>Delivery confirmation photo</p>
              </div>
            </div>
          ) : (
            <div className="flex-1 bg-gray-100 flex items-center justify-center">
              <div className="text-center">
                <MapPin size={36} className="mx-auto mb-2 text-amber-600" />
                <p className="text-gray-700">Delivery location map</p>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="relative flex-1">
          {/* Main image area */}
          <div className="w-full h-full bg-gray-100 flex items-center justify-center">
            <div className="text-center text-gray-400">
              <p>Delivery confirmation photo</p>
            </div>
          </div>
          
          {/* Small map overlay */}
          <div className="absolute bottom-4 left-4 w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center shadow-md">
            <MapPin size={24} className="text-amber-600" />
          </div>
          <div className="absolute bottom-4 right-4 bg-none rounded-md flex items-center justify-center">
            <button 
              className="bg-black rounded-full p-2 shadow-sm"
              onClick={() => {
                setIsExpanded(true);
                setActiveTab('photo');
              }}
            >
              <Maximize2 size={16} className='text-white'/>
            </button>
          </div>
        </div>
      )}
      
      {/* Delivery info section */}
      {!isExpanded && (
        <div className="p-5 bg-white items-center text-center">
          <h2 className="text-2xl font-bold mb-2">Enjoy your fresh bloom</h2>
          <p className="text-gray-600 mb-4">
            Thank you for your order! Your next delivery is currently scheduled at
          </p>
          
          {/* Date & time info */}
          <div className="flex items-center bg-gray-100 p-2 px-4 rounded-full mb-4 w-fit items-center justify-center mx-auto">
            <div className="mr-2">
              <Calendar size={20}/>
            </div>
            <div>
              <span className="font-medium">24 Mar 2025, Morning</span>
            </div>
          </div>
          <p className="text-sm text-gray-500 mb-6">You can change date in settings.</p>
          
          {/* Rate button */}
          <button 
            onClick={handleRateOrder}
            className="w-full p-4 bg-amber-300 text-black rounded-xl font-medium text-center border-none"
          >
            Rate this order
          </button>
        </div>
      )}
    </div>
  );
} 